import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/data/site";

const NAV = [
  { id: "services", label: "Services" },
  { id: "benefits", label: "Why Us" },
  { id: "voices", label: "Voices" },
  { id: "quote", label: "Quote" },
  { id: "contact", label: "Contact" },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("hero")}
          className="flex items-center gap-3 group"
        >
          <span className="block w-9 h-9 border border-[#D4AF37] rotate-45 relative">
            <span className="absolute inset-1 bg-[#D4AF37]/10" />
          </span>
          <span className="font-display text-white text-lg tracking-wide">
            <span className="font-light">Cutting</span>{" "}
            <span className="gold-text font-medium">Edge</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => handleNav(item.id)}
              className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors duration-300 tracking-wide"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            {SITE.phone}
          </a>
          <button
            data-testid="nav-cta"
            onClick={() => handleNav("quote")}
            className="btn-gold px-5 py-2.5 text-xs"
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white -mr-2 p-2"
          aria-label="menu"
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden absolute inset-x-0 top-20 h-[calc(100dvh-5rem)] bg-[#050505] border-t border-white/5 px-6 pt-8 pb-10 flex flex-col overflow-y-auto"
        >
          <div className="flex flex-col divide-y divide-white/5">
            {NAV.map((item, i) => (
              <button
                key={item.id}
                data-testid={`nav-mobile-link-${item.id}`}
                onClick={() => handleNav(item.id)}
                className="text-left py-4 flex items-center justify-between text-white/85 active:text-[#D4AF37] font-display text-2xl tracking-tight"
              >
                {item.label}
                <span className="text-[10px] tracking-[0.3em] text-white/30">
                  0{i + 1}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-auto pt-8 flex flex-col gap-3">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="nav-mobile-phone"
              className="btn-outline-gold px-5 py-4 text-xs flex items-center justify-center gap-2"
            >
              <Phone className="w-3.5 h-3.5" />
              Call {SITE.phone}
            </a>
            <button
              data-testid="nav-mobile-cta"
              onClick={() => handleNav("quote")}
              className="btn-gold px-5 py-4 text-xs"
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
